import {Component, effect, inject, input, signal} from "@angular/core";
import {Color, LegendPosition, LineChartModule, NgxChartsModule, ScaleType, Series} from "@swimlane/ngx-charts";
import {sevenDays, threeDays, twentyFourHours} from "src/app/presentation/common/globals";
import {
  calculateSeries
} from "src/app/presentation/main-view/information-center/animals/animal-details/animal-activity/animal-activity-calculation";
import {AnimalsStore} from "src/app/domain/assets/animals/animals.store";
import {
  AggregatedAnimalActivityApiClient
} from "src/app/infrastructure/moovement-api/aggregated-animal-activity.api-client";
import {AggregatedAnimalActivity} from "src/app/domain/assets/animals/aggregated-animal-activity";
import {ascending} from "d3";

export enum DateRangeName {
  TWENTY_FOUR_HOURS = '24 Hours',
  THREE_DAYS = '3 Days',
  SEVEN_DAYS = '7 Days'
}

export enum Context {
  HERD = "Herd activity",
  HISTORICAL = "Historical activity"
}

@Component({
  selector: 'app-animal-activity',
  imports: [
    LineChartModule,
    NgxChartsModule
  ],
  styleUrl: './animal-activity.component.css',
  templateUrl: './animal-activity.component.html'
})
export class AnimalActivityComponent {

  animalId = input<string>();

  dateRanges: Map<DateRangeName, number> = new Map<DateRangeName, number>();
  selectableDateRanges = signal<DateRangeName[]>([])
  selectedDateRangeName = signal<DateRangeName | undefined>(undefined);

  selectableContexts = signal<Context[]>([]);
  selectedContext = signal<Context>(Context.HISTORICAL);

  primaryActivityColor = '#217dd9';
  primaryActivity = signal<string | undefined>(undefined);
  secondaryActivityColor = 'rgba(192, 215, 229, 1)';
  secondaryActivity = signal<string | undefined>(undefined);

  activity: Series[] = [];
  scheme: Color = {
    name: 'default',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: [this.secondaryActivityColor, this.primaryActivityColor]
  }
  legendPosition = LegendPosition.Below;

  loading: boolean = false;

  private animalIds = signal<string[] | undefined>(undefined);

  private animalsStore = inject(AnimalsStore);
  private aggregatedAnimalActivityService = inject(AggregatedAnimalActivityApiClient);
  protected xAxisTicks: Date[] = [];
  protected xAxisTickFormatting = (date: Date) => date.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });

  constructor() {
    this.dateRanges
      .set(DateRangeName.TWENTY_FOUR_HOURS, twentyFourHours)
      .set(DateRangeName.THREE_DAYS, threeDays)
      .set(DateRangeName.SEVEN_DAYS, sevenDays);

    this.selectDateRange(undefined);
    this.selectContext(Context.HISTORICAL);

    effect(() => {
      if (this.animalsStore.loading()) return;

      const animals = this.animalsStore.entities();
      const animalIds = animals.map(animal => animal.id);
      this.animalIds.set(animalIds);
    });

    effect(() => {
      const animalId = this.animalId();
      const animalIds = this.animalIds();
      if (!animalId || !animalIds) return;

      const dateRangeName = this.selectedDateRangeName();
      if (!dateRangeName) return;

      const end = new Date().getTime();
      const selectedDateRange = this.dateRanges.get(dateRangeName)!;
      const selectedContext = this.selectedContext();

      this.loading = true;
      this.getAggregatedActivities(animalId, animalIds, end, selectedDateRange, selectedContext).subscribe({
        next: (aggregatedActivities) => {
          this.xAxisTicks = this.getTicks(aggregatedActivities);
          this.activity = calculateSeries(aggregatedActivities, animalId, end, selectedDateRange, selectedContext);
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        }
      });
    });
  }

  private getTicks(aggregatedActivities: AggregatedAnimalActivity[]) {
    const firstSampleByDay = new Map<string, Date>();
    for (let aggregatedAnimalActivity of aggregatedActivities) {
      for (let accelerationCount of aggregatedAnimalActivity.accelerationCounts) {
        const date = new Date(accelerationCount!.instant!);
        const day = date.toDateString();
        const firstSampleOfDay = firstSampleByDay.get(day);
        if (!firstSampleOfDay || date.getTime() < firstSampleOfDay.getTime()) {
          firstSampleByDay.set(day, date);
        }
      }
    }
    return Array.from(firstSampleByDay.values()).sort((a: Date, b: Date) => a.getTime() - b.getTime());
  }

  selectDateRange(dateRangeName?: DateRangeName) {
    this.selectedDateRangeName.set(dateRangeName);
    this.selectableDateRanges.set(Array.from(this.dateRanges).filter(dateRange => dateRange[0] != dateRangeName).map(it => it[0]));
  }

  selectContext(context: Context) {
    if (context === Context.HERD) {
      this.primaryActivity.set('Animal activity');
      this.secondaryActivity.set('Herd activity');
    } else if (context === Context.HISTORICAL) {
      this.primaryActivity.set('Activity in selected period');
      this.secondaryActivity.set('Activity in previous period');
    }
    this.selectedContext.set(context);
    this.selectableContexts.set(Object.values<Context>(Context).filter(c => c !== context));
  }

  private getAggregatedActivities(animalId: string, allAnimalIds: string[], end: number, dateRange: number, context?: Context) {
    let start;
    let animalIds;
    switch (context) {
      case undefined: {
        animalIds = [animalId];
        start = new Date(end - dateRange);
        break;
      }
      case Context.HISTORICAL: {
        animalIds = [animalId];
        start = new Date(end - dateRange * 2);
        break;
      }
      case Context.HERD: {
        animalIds = [animalId, ...allAnimalIds];
        start = new Date(end - dateRange);
        break;
      }
    }
    return this.aggregatedAnimalActivityService.getAggregatedAnimalActivities(animalIds, start);
  }
}
